const A4_WIDTH_TWIP = 11906;
const A4_HEIGHT_TWIP = 16838;
const PAGE_MARGIN_TWIP = 498;
const CONTENT_WIDTH_TWIP = A4_WIDTH_TWIP - PAGE_MARGIN_TWIP * 2;
const ROW_HEIGHT_TWIP = 450;
const FIRST_PAGE_ANSWER_ROWS = 30;
const NORMAL_PAGE_ANSWER_ROWS = 34;
const PAGE_GROUP_SIZE = 4;
const MAX_LINE_WIDTH = 63;
const MINISTRY_MAX_LINE_WIDTH = 49;
const MINISTRY_FIRST_PAGE_ANSWER_ROWS = 30;
const MINISTRY_NORMAL_PAGE_ANSWER_ROWS = 34;
const MINISTRY_MINIMUM_PAGE_COUNT = 2;
const MINISTRY_ROW_HEIGHT_TWIP = 462;
const MINISTRY_GUTTER_WIDTH_TWIP = 1450;
const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const {
  ANSWER_TEMPLATE,
  normalizeAnswerTemplate,
} = require("../constants/answerTemplates");

const getTemplateConfig = (templateType) => {
  const normalizedTemplateType = normalizeAnswerTemplate(templateType);

  if (normalizedTemplateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE) {
    return {
      templateType: normalizedTemplateType,
      firstPageRows: MINISTRY_FIRST_PAGE_ANSWER_ROWS,
      normalPageRows: MINISTRY_NORMAL_PAGE_ANSWER_ROWS,
      minimumPageCount: MINISTRY_MINIMUM_PAGE_COUNT,
      pageGroupSize: 1,
      maxLineWidth: MINISTRY_MAX_LINE_WIDTH,
      rowHeight: MINISTRY_ROW_HEIGHT_TWIP,
      fontSize: 20,
      characterSpacing: 3,
      textWidth: CONTENT_WIDTH_TWIP - MINISTRY_GUTTER_WIDTH_TWIP - 240,
    };
  }

  return {
    templateType: normalizedTemplateType,
    firstPageRows: FIRST_PAGE_ANSWER_ROWS,
    normalPageRows: NORMAL_PAGE_ANSWER_ROWS,
    minimumPageCount: PAGE_GROUP_SIZE,
    pageGroupSize: PAGE_GROUP_SIZE,
    maxLineWidth: MAX_LINE_WIDTH,
    rowHeight: ROW_HEIGHT_TWIP,
    fontSize: 23,
    characterSpacing: 6,
    textWidth: CONTENT_WIDTH_TWIP - 200,
  };
};

const EMBEDDED_FONT_NAME = "Noto Sans KR";
const FONT = {
  ascii: EMBEDDED_FONT_NAME,
  hAnsi: EMBEDDED_FONT_NAME,
  eastAsia: EMBEDDED_FONT_NAME,
  cs: EMBEDDED_FONT_NAME,
};

const ALIGNMENTS = new Set(["left", "center", "right", "justify"]);
// CRA의 asset 규칙은 require가 선택한 .cjs를 JS가 아닌 파일 URL로 처리한다.
// import 조건의 .mjs를 사용해야 배포 빌드에서도 AlignmentType 등을 얻는다.
// docx는 CRA의 Babel과 호환되는 9.6.1로 고정한다 (9.7.x ImageRun 변환 오류).
const loadDocx = async () => import("docx");

// Noto Sans KR의 한글 advance는 0.92em. 양식별 본문 크기와 자간으로
// 49자가 본문 폭 안에 들어가고 50자는 넘도록 맞춘다. 폭이 더 큰 특수문자
// 등은 브라우저의 동일 글꼴로 측정한다. fitText로 이 폭을 명시해야
// Word가 다른 한글 글꼴로 대체하는 환경에서도 49자 행을 다시 꺾지 않는다.
const createRunLayout = async (config) => {
  const sizePt = config.fontSize / 2;
  let context;
  if (typeof document !== "undefined" && document.fonts) {
    await document.fonts.load(`${sizePt}px "${EMBEDDED_FONT_NAME}"`);
    context = document.createElement("canvas").getContext("2d");
    if (context) context.font = `${sizePt}px "${EMBEDDED_FONT_NAME}"`;
  }
  return (text) => {
    const spacing = Array.from(text).length * config.characterSpacing;
    const width = context
      ? context.measureText(text).width * 20
      : Array.from(text).length * sizePt * 20 * 0.92;
    return {
      width: Math.max(
        1,
        Math.min(config.textWidth - 20, Math.round(width + spacing)),
      ),
      // LibreOffice 등 fitText를 무시하는 리더도 넓은 특수문자를 자르지 않는다.
      scale: Math.max(
        1,
        Math.min(
          100,
          Math.floor(((config.textWidth - spacing - 20) / (width || 1)) * 100),
        ),
      ),
    };
  };
};

const normalizeAlignment = (alignment) =>
  ALIGNMENTS.has(alignment) ? alignment : "left";

const wrapText = (text, maxWidth = MAX_LINE_WIDTH) => {
  if (!text) return [""];
  const characters = Array.from(text);
  const wrapped = [];

  for (let index = 0; index < characters.length; index += maxWidth) {
    wrapped.push(characters.slice(index, index + maxWidth).join(""));
  }

  return wrapped;
};

const loadEmbeddedFontData = async () => {
  if (typeof fetch !== "function") return null;

  try {
    const fontAsset = require("../assets/NotoSansKR-Regular.otf");
    const fontUrl = fontAsset?.default || fontAsset;
    const response = await fetch(fontUrl);
    if (!response.ok) return null;
    return new Uint8Array(await response.arrayBuffer());
  } catch (error) {
    console.warn("Word 내보내기용 내장 글꼴을 불러오지 못했습니다.", error);
    return null;
  }
};

const wrapAnswerBlocks = (blocks, templateType) => {
  const { maxLineWidth } = getTemplateConfig(templateType);

  return blocks.flatMap(({ text = "", alignment = "left" }) =>
    String(text)
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .flatMap((line) =>
        wrapText(line, maxLineWidth).map((wrappedLine) => ({
          text: wrappedLine,
          alignment: normalizeAlignment(alignment),
        })),
      ),
  );
};

const parseAnswerHtml = (html, domDocument, templateType) => {
  const documentRef =
    domDocument || (typeof document !== "undefined" ? document : null);

  if (!documentRef) {
    throw new Error("Word export requires a browser document.");
  }

  const wrapper = documentRef.createElement("div");
  wrapper.innerHTML = html || "";

  const blocks = Array.from(wrapper.childNodes).flatMap((node) => {
    if (node.nodeType === 3) {
      return node.textContent
        ? [{ text: node.textContent, alignment: "left" }]
        : [];
    }

    if (node.nodeType !== 1) return [];

    const clone = node.cloneNode(true);
    clone.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));

    const text = clone.textContent || "";

    return [
      {
        text: /^\n+$/.test(text) ? "" : text,
        alignment: normalizeAlignment(node.style?.textAlign),
      },
    ];
  });

  return wrapAnswerBlocks(
    blocks.length ? blocks : [{ text: "" }],
    templateType,
  );
};

const paginateAnswerLines = (lines, templateType) => {
  const { firstPageRows, normalPageRows, minimumPageCount, pageGroupSize } =
    getTemplateConfig(templateType);
  const source = lines.length ? [...lines] : [{ text: "", alignment: "left" }];
  const pages = [];

  pages.push(source.splice(0, firstPageRows));
  while (source.length) {
    pages.push(source.splice(0, normalPageRows));
  }

  const pageCount = Math.max(
    minimumPageCount,
    Math.ceil(pages.length / pageGroupSize) * pageGroupSize,
  );
  while (pages.length < pageCount) pages.push([]);
  return pages;
};

const sanitizeFilename = (subject) => {
  const safeSubject = String(subject || "답안지")
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);

  return `${safeSubject || "답안지"}_답안지.docx`;
};

const createAnswerDocument = async ({
  html,
  blocks,
  subject,
  templateType,
  embeddedFontData = null,
}) => {
  const templateConfig = getTemplateConfig(templateType);
  const isMinistryTemplate =
    templateConfig.templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE;
  const docx = await loadDocx();
  const {
    AlignmentType,
    BorderStyle,
    BuilderElement,
    Document,
    HeightRule,
    LineRuleType,
    Paragraph,
    SectionType,
    ShadingType,
    Table,
    TableCell,
    TableLayoutType,
    TableRow,
    TextRun,
    VerticalAlign,
    WidthType,
  } = docx;

  const lines = blocks
    ? wrapAnswerBlocks(blocks, templateConfig.templateType)
    : parseAnswerHtml(html, undefined, templateConfig.templateType);
  const pages = paginateAnswerLines(lines, templateConfig.templateType);
  const runLayout = await createRunLayout(templateConfig);
  let fitTextId = 0;

  // docx에는 fitText 옵션이 없어 TextRun의 보호된 속성 확장 지점을 사용한다.
  // 각 행에 고유 ID를 부여해 Word가 이웃 행을 같은 압축 그룹으로 합치지 않는다.
  class AnswerTextRun extends TextRun {
    constructor(options) {
      const layout = runLayout(options.text);
      super({ ...options, scale: layout.scale });
      if (options.text) {
        this.properties.push(
          new BuilderElement({
            name: "w:fitText",
            attributes: {
              width: { key: "w:val", value: layout.width },
              id: { key: "w:id", value: ++fitTextId },
            },
          }),
        );
      }
    }
  }

  const border = (color, size = 4) => ({
    style: BorderStyle.SINGLE,
    color,
    size,
  });

  const alignmentMap = {
    left: AlignmentType.LEFT,
    center: AlignmentType.CENTER,
    right: AlignmentType.RIGHT,
    justify: AlignmentType.JUSTIFIED,
  };

  const createParagraph = ({
    text = "",
    alignment = "left",
    bold = false,
    color = "000000",
    size = 22,
    answer = false,
  }) =>
    new Paragraph({
      alignment: alignmentMap[normalizeAlignment(alignment)],
      autoSpaceEastAsianText: false,
      spacing: {
        before: 0,
        after: 0,
        line: 280,
        lineRule: LineRuleType.EXACT,
      },
      children: [
        new (answer ? AnswerTextRun : TextRun)({
          text,
          bold,
          color,
          size,
          font: FONT,
          snapToGrid: false,
          ...(answer
            ? {
                size: templateConfig.fontSize,
                characterSpacing: templateConfig.characterSpacing,
              }
            : {}),
        }),
      ],
    });

  const createRow = ({
    line,
    fill,
    topBorder = "D9D9D9",
    bottomBorder = "D9D9D9",
    header = false,
  }) =>
    new TableRow({
      cantSplit: true,
      height: { value: ROW_HEIGHT_TWIP, rule: HeightRule.EXACT },
      children: [
        new TableCell({
          width: { size: CONTENT_WIDTH_TWIP, type: WidthType.DXA },
          verticalAlign: VerticalAlign.CENTER,
          margins: {
            top: 0,
            bottom: 0,
            left: 100,
            right: 100,
            marginUnitType: WidthType.DXA,
          },
          shading: {
            fill,
            color: "auto",
            type: ShadingType.CLEAR,
          },
          borders: {
            top: border(topBorder),
            bottom: border(bottomBorder),
            left: border("555555", 6),
            right: border("555555", 6),
          },
          children: [
            createParagraph({
              ...line,
              bold: header,
              size: header ? 26 : 22,
              answer: !header,
            }),
          ],
        }),
      ],
    });

  const createPageTable = (pageLines, pageIndex) => {
    const isFirstPage = pageIndex === 0;
    const capacity = isFirstPage
      ? FIRST_PAGE_ANSWER_ROWS
      : NORMAL_PAGE_ANSWER_ROWS;
    const pageFill = pageIndex % 2 === 0 ? "FFFFFF" : "F3F7FF";
    const paddedLines = [...pageLines];

    while (paddedLines.length < capacity) {
      paddedLines.push({ text: "", alignment: "left" });
    }

    const rows = [];

    if (isFirstPage) {
      rows.push(
        createRow({
          line: {
            text: "문형",
            alignment: "center",
            color: "FFFFFF",
          },
          fill: "003664",
          topBorder: "555555",
          bottomBorder: "003664",
          header: true,
        }),
        createRow({
          line: {
            text: "사례형",
            alignment: "center",
            color: "003664",
          },
          fill: "E3EFFF",
          header: true,
        }),
      );
    }

    paddedLines.forEach((line, rowIndex) => {
      rows.push(
        createRow({
          line,
          fill: pageFill,
          topBorder: rowIndex === 0 && !isFirstPage ? "555555" : "D9D9D9",
          bottomBorder:
            rowIndex === paddedLines.length - 1 ? "555555" : "D9D9D9",
        }),
      );
    });

    return new Table({
      rows,
      width: { size: CONTENT_WIDTH_TWIP, type: WidthType.DXA },
      columnWidths: [CONTENT_WIDTH_TWIP],
      layout: TableLayoutType.FIXED,
      alignment: AlignmentType.CENTER,
    });
  };

  const createMinistryPageTable = (pageLines, pageIndex) => {
    const capacity =
      pageIndex === 0
        ? templateConfig.firstPageRows
        : templateConfig.normalPageRows;
    const firstLineNumber =
      pageIndex === 0
        ? 1
        : templateConfig.firstPageRows +
          (pageIndex - 1) * templateConfig.normalPageRows +
          1;
    const paddedLines = [...pageLines];

    while (paddedLines.length < capacity) {
      paddedLines.push({ text: "", alignment: "left" });
    }

    const rows = paddedLines.map((line, rowIndex) => {
      const lineNumber = firstLineNumber + rowIndex;
      const marker =
        rowIndex === 0
          ? `(제1문-${pageIndex + 1}쪽) ${lineNumber}`
          : lineNumber % 5 === 0
            ? String(lineNumber)
            : "";
      const pageFill = pageIndex % 2 === 0 ? "F7F7F7" : "E3EBF1";

      return new TableRow({
        cantSplit: true,
        height: {
          value: templateConfig.rowHeight,
          rule: HeightRule.EXACT,
        },
        children: [
          new TableCell({
            width: {
              size: MINISTRY_GUTTER_WIDTH_TWIP,
              type: WidthType.DXA,
            },
            verticalAlign: VerticalAlign.CENTER,
            margins: { top: 0, bottom: 0, left: 60, right: 80 },
            shading: {
              fill: "FFFFFF",
              color: "auto",
              type: ShadingType.CLEAR,
            },
            borders: {
              top: border("FFFFFF", 0),
              bottom: border("FFFFFF", 0),
              left: border("FFFFFF", 0),
              right: border("FFFFFF", 0),
            },
            children: [
              createParagraph({
                text: marker,
                alignment: "right",
                color: "52616F",
                size: 18,
                bold: rowIndex === 0,
              }),
            ],
          }),
          new TableCell({
            width: {
              size: CONTENT_WIDTH_TWIP - MINISTRY_GUTTER_WIDTH_TWIP,
              type: WidthType.DXA,
            },
            verticalAlign: VerticalAlign.CENTER,
            margins: { top: 0, bottom: 0, left: 120, right: 120 },
            shading: {
              fill: pageFill,
              color: "auto",
              type: ShadingType.CLEAR,
            },
            borders: {
              top: border("19162B", 4),
              bottom: border("19162B", 4),
              left: border(pageFill, 0),
              right: border(pageFill, 0),
            },
            children: [createParagraph({ ...line, answer: true })],
          }),
        ],
      });
    });

    return new Table({
      rows,
      width: { size: CONTENT_WIDTH_TWIP, type: WidthType.DXA },
      columnWidths: [
        MINISTRY_GUTTER_WIDTH_TWIP,
        CONTENT_WIDTH_TWIP - MINISTRY_GUTTER_WIDTH_TWIP,
      ],
      layout: TableLayoutType.FIXED,
      alignment: AlignmentType.CENTER,
    });
  };

  const sections = pages.map((pageLines, pageIndex) => ({
    properties: {
      type: pageIndex === 0 ? undefined : SectionType.NEXT_PAGE,
      page: {
        size: {
          width: A4_WIDTH_TWIP,
          height: A4_HEIGHT_TWIP,
        },
        margin: {
          top: PAGE_MARGIN_TWIP,
          right: PAGE_MARGIN_TWIP,
          bottom: PAGE_MARGIN_TWIP,
          left: PAGE_MARGIN_TWIP,
          header: 0,
          footer: 0,
          gutter: 0,
        },
      },
    },
    children: [
      isMinistryTemplate
        ? createMinistryPageTable(pageLines, pageIndex)
        : createPageTable(pageLines, pageIndex),
      // Word가 마지막 표 뒤에 자동으로 넣는 일반 크기 문단 때문에
      // 법무부 34행 다음에 빈 페이지가 생기지 않도록 끝 문단을 명시한다.
      ...(pageIndex === pages.length - 1
        ? [
            new Paragraph({
              spacing: {
                before: 0,
                after: 0,
                line: 1,
                lineRule: LineRuleType.EXACT,
              },
              run: { size: 2 },
            }),
          ]
        : []),
    ],
  }));

  return new Document({
    title: subject || "답안지",
    subject: "IBT 연습 답안",
    creator: "IBT 연습장",
    description: `IBT 연습장에서 작성한 ${
      isMinistryTemplate ? "법무부" : "법전협"
    } 양식 답안지`,
    fonts: embeddedFontData
      ? [{ name: EMBEDDED_FONT_NAME, data: embeddedFontData }]
      : undefined,
    styles: {
      default: {
        document: {
          run: {
            font: FONT,
            size: 22,
            color: "000000",
          },
          paragraph: {
            spacing: { before: 0, after: 0 },
          },
        },
      },
    },
    sections,
  });
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const exportAnswerToWord = async ({ html, subject, templateType }) => {
  const blob = await createAnswerWordBlob({ html, subject, templateType });
  const filename = sanitizeFilename(subject);

  downloadBlob(blob, filename);
  return filename;
};

// docx 9.6.1은 글꼴 이름을 그대로 ZIP 경로에 넣는다. 공백이 있는
// 'fonts/Noto Sans KR.odttf'는 Word에서 복구 경고를 일으키므로 OPC에
// 안전한 경로와 관계로 함께 변경한다. 실제 글꼴 이름/바이너리는 유지한다.
const packAnswerDocument = async (wordDocument) => {
  const [{ Packer }, { default: JSZip }] = await Promise.all([
    loadDocx(),
    import("jszip"),
  ]);
  const zip = await JSZip.loadAsync(await Packer.toArrayBuffer(wordDocument));
  const relsPath = "word/_rels/fontTable.xml.rels";
  let rels = await zip.file(relsPath).async("string");
  const fontParts = Object.keys(zip.files).filter((name) =>
    /^word\/fonts\/[^/]+\.odttf$/.test(name),
  );
  for (const [index, oldPath] of fontParts.entries()) {
    const newPath = `word/fonts/embedded-${index + 1}.odttf`;
    const data = await zip.file(oldPath).async("uint8array");
    zip.remove(oldPath);
    zip.file(newPath, data);
    rels = rels.replace(
      `Target="${oldPath.slice(5)}"`,
      `Target="${newPath.slice(5)}"`,
    );
  }
  zip.file(relsPath, rels);
  const fontTable = await zip.file("word/fontTable.xml").async("string");
  zip.file(
    "word/fontTable.xml",
    fontTable.replace(
      /w:fontKey="([^"]+)"/g,
      (_, key) => `w:fontKey="${key.toUpperCase()}"`,
    ),
  );
  return zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
};

const createAnswerWordBlob = async ({ html, subject, templateType }) => {
  const embeddedFontData = await loadEmbeddedFontData();
  const wordDocument = await createAnswerDocument({
    html,
    subject,
    templateType,
    embeddedFontData,
  });
  return new Blob([await packAnswerDocument(wordDocument)], {
    type: DOCX_MIME_TYPE,
  });
};

module.exports = {
  createAnswerDocument,
  createAnswerWordBlob,
  exportAnswerToWord,
  getTemplateConfig,
  loadEmbeddedFontData,
  paginateAnswerLines,
  packAnswerDocument,
  parseAnswerHtml,
  sanitizeFilename,
  wrapAnswerBlocks,
};
